/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
} from '@material-ui/core';

import {
  selectCompany,
  selectIsPending,
} from '../../store/widgetSlice';

import {
  updateCompany,
} from '../../store/companySlice';

import {
  setMessage,
} from '../../store/authSlice';

import {
  validateField,
  validateForm,
} from '../../helpers/validation';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  elementHidden: {
    display: 'none',
  },
}));

export default function SettingsForm() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const company = useSelector(selectCompany);
  const isPending = useSelector(selectIsPending);
  const [formErrors, setFormErrors] = useState({});
  const [formValues, setFormValues] = useState({
    verificationMessage: '',
    welcomeMessage: '',
    customAttributes: {
      firstNameCheck: '',
      ageMinimum: 0,
      maxAttempts: 3,
    },
  });

  const formRules = {
    verificationMessage: [],
    welcomeMessage: [],
    firstNameCheck: [],
    ageMinimum: [],
  };

  const handleChange = (event) => {
    setFormValues({ ...formValues, [event.target.name]: event.target.value });
  };

  const handleCustomAttributeChange = (event) => {
    setFormValues({
      ...formValues,
      customAttributes: {
        ...formValues.customAttributes,
        [event.target.name]: event.target.value,
      },
    });
  };

  const handleBlur = (event) => {
    validateField(formRules, formErrors, setFormErrors, event.target.name, event.target.value);
  };

  useEffect(() => {
    setFormValues((_formValues) => ({
      ..._formValues,
      ...company,
    }));
  },
  [company]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (validateForm(formRules, formValues, formErrors, setFormErrors)) {
      dispatch(updateCompany(formValues))
        .unwrap()
        .then(() => {
          dispatch(setMessage({ message: 'Company settings updated', severity: 'success' }));
        })
        .catch(() => {
          dispatch(setMessage({ message: 'Company settings could not be updated', severity: 'error' }));
        });
    }
  };

  return (
    <form className={classes.form}>
      <h1>Settings</h1>
      <h2>Custom Verification Message</h2>
      <p>
        <strong>Available Tags:</strong>
        {' '}
        <pre>{'{guestName} - Guest\'s first name e.g. John' }</pre>
        <pre>{'{companyName} - Your company name e.g. XYZ Vacation Rentals' }</pre>
        <pre>{'{reference} - Reference code of verification session e.g. LLE5979L3H' }</pre>
        <pre>{'{link} - Verification session link e.g. https://app.guestranger.com/verify/B2TSMHX2VA' }</pre>
      </p>
      <TextField
        variant="outlined"
        margin="normal"
        id="verification-message"
        label="Verification Message"
        name="verificationMessage"
        value={formValues.verificationMessage}
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!formErrors.verificationMessage}
        helperText={formErrors.verificationMessage}
        disabled={isPending}
        minRows={5}
        multiline
        fullWidth
      />
      <h2>Custom Welcome Message</h2>
      <p>
        <strong>Available Tags:</strong>
        {' '}
        <pre>{'{companyName} - Your company name e.g. XYZ Vacation Rentals' }</pre>
      </p>
      <TextField
        variant="outlined"
        margin="normal"
        id="welcome-message"
        label="Welcome Message"
        name="welcomeMessage"
        value={formValues.welcomeMessage}
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!formErrors.welcomeMessage}
        helperText={formErrors.welcomeMessage}
        disabled={isPending}
        minRows={5}
        multiline
        fullWidth
      />
      <h2>Identity Verification Restrictions</h2>
      <TextField
        variant="outlined"
        margin="normal"
        id="age-minimum"
        label="Age Minimum"
        name="ageMinimum"
        value={formValues.customAttributes && formValues.customAttributes.ageMinimum}
        onChange={handleCustomAttributeChange}
        onBlur={handleBlur}
        error={!!formErrors.ageMinimum}
        helperText={formErrors.ageMinimum}
        disabled={isPending}
        fullWidth
      />
      <FormControl margin="normal" variant="outlined" fullWidth required>
        <InputLabel id="guest-country-label">First Name Check</InputLabel>
        <Select
          label="First Name Check"
          labelId="first-name-check"
          variant="outlined"
          margin="normal"
          id="firstNameCheck"
          name="firstNameCheck"
          value={formValues.customAttributes && formValues.customAttributes.firstNameCheck}
          error={!!formErrors.firstNameCheck}
          helperText={formErrors.firstNameCheck}
          onChange={handleCustomAttributeChange}
          onBlur={handleBlur}
          disabled={isPending}
          fullWidth
          required
        >
          <MenuItem value="STRICT">
            Strict
          </MenuItem>
          <MenuItem value="LENIENT">
            Lenient
          </MenuItem>
        </Select>
      </FormControl>
      <TextField
        variant="outlined"
        margin="normal"
        id="max-attempts"
        label="Max Attempts"
        name="maxAttempts"
        value={formValues.customAttributes && formValues.customAttributes.maxAttempts}
        onChange={handleCustomAttributeChange}
        onBlur={handleBlur}
        error={!!formErrors.maxAttempts}
        helperText={formErrors.maxAttempts}
        disabled={isPending}
        fullWidth
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        className={classes.submit}
        onClick={handleSubmit}
        disabled={isPending}
        fullWidth
      >
        Submit
      </Button>
    </form>
  );
}
